import type { Domain, DnsRecord } from '../types/domain.types';
import type { DnsZoneResponse, DnsMutationRecord } from '../types/dns.types';
import { supabase } from '@/shared/lib/supabase';
import { getPartnerToken } from '@afrilink/config';

/**
 * Domain service — Phase 8e.3.
 *
 * Reads `domain_link` rows + live registry state via the Supabase Edge
 * Functions deployed in Phase 8e.2:
 *
 *   - `domain-list` — returns the customer's domains, hydrated from
 *     domains.co.za /domain/list (cached 15 min server-side).
 *   - `domain-info` — full info for one domain, hydrated from /domain
 *     (cached 5 min server-side).
 *
 * DNS records remain `[]` for Phase 8e: AD-26 deferred the DNS editor.
 * Customers manage DNS in cPanel until that slice ships.
 */

interface DomainListResponse {
  domains: Domain[];
  staleAt: string;
}

interface DomainInfoResponse {
  domain: Domain;
  staleAt: string;
}

interface ApiErrorBody {
  code?: string;
  message?: string;
}

/**
 * Transfer-confirm error carries the EF code so the wizard can branch on
 * specific codes (e.g. tld_not_supported → show waitlist CTA).
 */
export class TransferConfirmError extends Error {
  constructor(message: string, public readonly code: string) {
    super(message);
    this.name = 'TransferConfirmError';
  }
}

/**
 * Thrown by `checkAvailability` when the TLD is outside our allow-list.
 * Callers should show the gTLD waitlist CTA instead of a generic error.
 */
export class TldNotSupportedError extends Error {
  constructor(message: string, public readonly requested: string, public readonly allowed: string[]) {
    super(message);
    this.name = 'TldNotSupportedError';
  }
}

function unwrapInvokeError(prefix: string, error: unknown): never {
  if (error && typeof error === 'object' && 'message' in error) {
    throw new Error(`${prefix}: ${(error as { message: string }).message}`);
  }
  throw new Error(`${prefix}: ${String(error)}`);
}

export const domainService = {
  async list(): Promise<Domain[]> {
    const { data, error } = await supabase.functions.invoke<DomainListResponse>(
      'domain-list',
      { method: 'GET' },
    );
    if (error) unwrapInvokeError('Could not load domains', error);
    return data?.domains ?? [];
  },

  async getByName(name: string): Promise<Domain | null> {
    const { data, error } = await supabase.functions.invoke<DomainInfoResponse>(
      'domain-info',
      { method: 'POST', body: { domain: name } },
    );
    if (error) {
      // 404 = caller does not own this domain (or it's not in domain_link).
      // The Edge Function returns 404 with code='domain_not_linked'; treat as "not found".
      const body = (error as { context?: { body?: ApiErrorBody } }).context?.body;
      if (body?.code === 'domain_not_linked') return null;
      unwrapInvokeError(`Could not load ${name}`, error);
    }
    return data?.domain ?? null;
  },

  async getById(id: string): Promise<Domain | null> {
    // No EF endpoint for "get by id" — list and filter. Acceptable: list is
    // cached server-side; this is only used as a fallback path.
    const all = await this.list();
    return all.find((d) => d.id === id) ?? null;
  },

  /**
   * AD-26 superseded by G1 (2026-05-05) — DNS editor live for domains
   * pointed at SightHost default nameservers via cPanel.
   *
   * Returns the editable zone records + a banner state telling the UI
   * whether the customer can edit them. When NS isn't SightHost-default,
   * `editable=false` and `warning` carries the friendly copy to render.
   */
  async listDnsRecords(domain: string): Promise<DnsZoneResponse> {
    const { data, error } = await supabase.functions.invoke<DnsZoneResponse>('whm-dns-list', {
      method: 'POST',
      body: { domain },
    });
    if (error) {
      const body = (error as { context?: { body?: ApiErrorBody } }).context?.body;
      throw new Error(body?.message ?? (error as { message?: string }).message ?? 'Could not load DNS records.');
    }
    return data ?? { ok: true, ns_state: 'external', editable: false, nameservers: [], records: [] };
  },

  /**
   * Add / edit / delete a single DNS record. Idempotency-keyed.
   * Maps EF error codes to customer-friendly copy.
   */
  async dnsMutate(payload: {
    domain: string;
    action: 'add' | 'edit' | 'delete';
    record?: DnsMutationRecord;
    line?: number;
  }): Promise<{ ok: true; line?: number }> {
    const { data, error } = await supabase.functions.invoke('whm-dns-mutate', {
      method: 'POST',
      body: payload,
      headers: { 'idempotency-key': crypto.randomUUID() },
    });
    if (error) {
      const body = (error as { context?: { body?: ApiErrorBody } }).context?.body;
      const code = body?.code;
      let msg: string;
      switch (code) {
        case 'apex_ns_protected':
          msg = 'Apex NS records are managed via the Nameservers tab.';
          break;
        case 'whm_token_missing_dns_privilege':
          msg = "Our system can't update DNS right now. We've alerted the team.";
          break;
        case 'invalid_record':
          msg = body?.message ?? 'That record doesn\'t look right — check the format.';
          break;
        case 'not_owner':
          msg = body?.message ?? "You don't own this domain.";
          break;
        default:
          msg = body?.message ?? (error as { message?: string }).message ?? 'DNS update failed.';
      }
      throw new Error(msg);
    }
    return data;
  },

  async registerQuote(domain: string, periodYears = 1): Promise<{
    available: boolean;
    fqdn: string;
    sld: string;
    tld: string;
    period_years: number;
    price_per_year_zar: number;
    total_zar: number;
    is_premium: boolean;
  }> {
    const { data, error } = await supabase.functions.invoke('domain-register-quote', {
      method: 'POST',
      body: { domain, period_years: periodYears },
    });
    if (error) {
      const body = (error as { context?: { body?: ApiErrorBody } }).context?.body;
      throw new Error(body?.message ?? (error as { message?: string }).message ?? 'Quote failed.');
    }
    return data;
  },

  async registerConfirm(payload: {
    domain: string;
    period_years?: number;
    bind_to_cpanel_username?: string;
    registrant: {
      name: string;
      email: string;
      contactNumber: string;
      address1: string;
      city: string;
      province: string;
      postalCode: string;
      country: string;
      organisation?: string;
    };
  }): Promise<{ invoice_id: string; invoice_number: string; pay_url: string; total_zar: number }> {
    // Partner attribution (2026-09-25): the customer, not the domain, is
    // attributed on their first commercial act. Resolved server-side.
    const partnerToken = getPartnerToken();
    const { data, error } = await supabase.functions.invoke('domain-register-confirm', {
      method: 'POST',
      body: partnerToken ? { ...payload, partner_token: partnerToken } : payload,
      headers: { 'idempotency-key': crypto.randomUUID() },
    });
    if (error) {
      const body = (error as { context?: { body?: ApiErrorBody } }).context?.body;
      throw new Error(body?.message ?? (error as { message?: string }).message ?? 'Register failed.');
    }
    return data;
  },

  async renew(domainLinkId: string, periodYears = 1): Promise<{ invoice_id: string; pay_url: string; total_zar: number }> {
    const { data, error } = await supabase.functions.invoke('domain-renew', {
      method: 'POST',
      body: { domain_link_id: domainLinkId, period_years: periodYears },
      headers: { 'idempotency-key': crypto.randomUUID() },
    });
    if (error) {
      const body = (error as { context?: { body?: ApiErrorBody } }).context?.body;
      throw new Error(body?.message ?? (error as { message?: string }).message ?? 'Renew failed.');
    }
    return data;
  },

  /**
   * DT-1 (2026-05-11) — customer entry point for domain mutations.
   *
   * Instead of executing live against domains.co.za, this creates a ticket
   * in the `domains` department. Staff executes within the 1-hour SLA.
   *
   * Returns ticket info + (for register/renew) the linked invoice + a
   * portal payment URL. Caller redirects to `redirect_url`
   * (i.e. /app/domains/intent/<ticket_id>).
   *
   * For friendly rejections (TLD not supported, domain unavailable,
   * already pending, etc.), the EF returns a 4xx with a typed code that
   * this method exposes via the error message.
   */
  async intentTicket(input: {
    operation: 'register' | 'renew' | 'transfer_in';
    payload: Record<string, unknown>;
  }): Promise<{
    ok: true;
    ticket_id: string;
    ticket_number: string;
    ticket_uuid_short: string;
    payment_required: boolean;
    invoice_id?: string;
    invoice_number?: string;
    total_amount?: number;
    payment_url?: string;
    redirect_url: string;
    message?: string;
  }> {
    const { data, error } = await supabase.functions.invoke('domain-intent-ticket', {
      method: 'POST',
      body: input,
      headers: { 'idempotency-key': crypto.randomUUID() },
    });
    if (error) {
      const body = (error as { context?: { body?: ApiErrorBody } }).context?.body;
      throw new Error(
        body?.message ?? (error as { message?: string }).message ?? 'Could not submit your request.',
      );
    }
    return data;
  },

  async toggleAutoRenew(domainLinkId: string, enabled: boolean): Promise<{ auto_renew: boolean }> {
    const { data, error } = await supabase.functions.invoke('domain-autorenew', {
      method: 'POST',
      body: { domain_link_id: domainLinkId, enabled },
      headers: { 'idempotency-key': crypto.randomUUID() },
    });
    if (error) {
      const body = (error as { context?: { body?: ApiErrorBody } }).context?.body;
      throw new Error(body?.message ?? (error as { message?: string }).message ?? 'Toggle failed.');
    }
    return data;
  },

  /**
   * Check whether a domain is registrable. For transfer-in we want
   * `available === false` (the domain is currently registered elsewhere
   * — that's the prerequisite for transferring it in).
   *
   * Throws TldNotSupportedError when the TLD is outside our allow-list,
   * so callers can offer the gTLD waitlist UX without parsing the
   * generic error message.
   */
  async checkAvailability(domain: string): Promise<{
    available: boolean;
    fqdn: string;
    sld: string;
    tld: string;
    isPremium: boolean;
    usesEppKey: boolean;
  }> {
    const { data, error } = await supabase.functions.invoke('domain-availability', {
      method: 'POST',
      body: { domain },
    });
    if (error) {
      const body = (error as { context?: { body?: ApiErrorBody & { allowed?: string[]; requested?: string } } }).context?.body;
      if (body?.code === 'tld_not_supported') {
        throw new TldNotSupportedError(body.message ?? 'TLD not yet supported.', body.requested ?? '', body.allowed ?? []);
      }
      throw new Error(body?.message ?? (error as { message?: string }).message ?? 'Availability check failed.');
    }
    return data;
  },

  /**
   * Add an entry to the gTLD waitlist for TLDs not yet in the allow-list.
   * Locked decision G3-Q4: capture customer email + FQDN so we can notify
   * when (e.g.) `.com` lands. Idempotent on (email, fqdn).
   */
  async addToGtldWaitlist(email: string, fqdn: string): Promise<{ ok: true }> {
    const { data, error } = await supabase.functions.invoke('gtld-waitlist-add', {
      method: 'POST',
      body: { email, fqdn },
      headers: { 'idempotency-key': crypto.randomUUID() },
    });
    if (error) {
      const body = (error as { context?: { body?: ApiErrorBody } }).context?.body;
      throw new Error(body?.message ?? (error as { message?: string }).message ?? 'Could not add to waitlist.');
    }
    return data;
  },

  /**
   * Initiate a transfer-in. Calls `domain-transfer-confirm` EF which:
   *   1. Validates registrant + balance + TLD allow-list.
   *   2. Rejects if FQDN already linked or has an active order.
   *   3. Creates `domain_registration_order(step='transfer')` + invoice.
   *   4. Returns the invoice + pay_url. The post-payment saga (fixed
   *      2026-05-03) handles the registrar request via provisioning-runner.
   *
   * Error codes mapped to customer-friendly copy here so the wizard
   * doesn't need its own translation table.
   */
  async transferConfirm(payload: {
    domain: string;
    epp_key?: string;
    registrant: {
      name: string;
      email: string;
      contactNumber: string;
      address1: string;
      city: string;
      province: string;
      postalCode: string;
      country: string;
      organisation?: string;
    };
  }): Promise<{
    ok: true;
    order_id: string;
    invoice_id: string;
    invoice_number: string;
    domain: string;
    total_zar: number;
    pay_url: string;
    next_step: string;
  }> {
    const { data, error } = await supabase.functions.invoke('domain-transfer-confirm', {
      method: 'POST',
      body: payload,
      headers: { 'idempotency-key': crypto.randomUUID() },
    });
    if (error) {
      const body = (error as { context?: { body?: ApiErrorBody } }).context?.body;
      // Friendly copy per BRD G3 FR-10..12. Hide reseller balance from the
      // customer per FR-10 (operational detail, not customer-facing).
      const code = body?.code;
      let msg: string;
      switch (code) {
        case 'already_registered':
          msg = `${payload.domain} is already in your portal — no need to transfer it.`;
          break;
        case 'order_already_pending':
          msg = `There is already a transfer in progress for ${payload.domain}. Check your domains list for status.`;
          break;
        case 'tld_not_supported':
          msg = `We don't yet handle this TLD. We can let you know the moment we do — drop your email on the waitlist.`;
          break;
        case 'reseller_balance_too_low':
        case 'reseller_balance_under_total':
          msg = `Our system needs a top-up before we can process this transfer. We've alerted the team — please try again in a few minutes, or contact support if it keeps failing.`;
          break;
        case 'registrar_error_balance_check':
          msg = `We could not reach the registrar right now. Please try again in a moment.`;
          break;
        case 'invalid_registrant':
          msg = body?.message ?? 'Some registrant details are missing or malformed. Please check the form.';
          break;
        default:
          msg = body?.message ?? (error as { message?: string }).message ?? 'Could not start transfer.';
      }
      throw new TransferConfirmError(msg, code ?? 'unknown');
    }
    return data;
  },

  async sendEppCode(domainLinkId: string): Promise<{ message: string }> {
    const { data, error } = await supabase.functions.invoke('domain-epp', {
      method: 'POST',
      body: { domain_link_id: domainLinkId, method: 'send' },
      headers: { 'idempotency-key': crypto.randomUUID() },
    });
    if (error) {
      const body = (error as { context?: { body?: ApiErrorBody } }).context?.body;
      throw new Error(body?.message ?? (error as { message?: string }).message ?? 'EPP send failed.');
    }
    return data;
  },

  /**
   * Customer-side NS edit (Sprint 2.5). EF was opened to dual-mode auth on
   * 2026-05-05; ownership is enforced server-side via domain_link.customer_id.
   */
  async updateNameservers(domainLinkId: string, nameservers: string[]): Promise<{ domain: string; nameservers: string[] }> {
    const { data, error } = await supabase.functions.invoke('domain-update-ns', {
      method: 'POST',
      body: { domain_link_id: domainLinkId, nameservers },
      headers: { 'idempotency-key': crypto.randomUUID() },
    });
    if (error) {
      const body = (error as { context?: { body?: ApiErrorBody } }).context?.body;
      throw new Error(body?.message ?? (error as { message?: string }).message ?? 'Nameserver update failed.');
    }
    return data;
  },
};
